/**
 * The resolution queue, out as a sheet and back as a decision (X-09 Stage 3).
 *
 * **Why a workbook and not a screen.** 7,878 products on one real client still
 * need a code, and the tail is flat rather than Pareto — the top 500 are half
 * the queue's turnover and most of the rest carry under ₹10,000. Per-product
 * clicking is not a job anyone finishes, and this practice already works in
 * Excel.
 *
 * **The upload is the largest write the product can make**, so the copy says
 * what it will do before it does it, and the service refuses a file with one
 * bad code rather than applying half of it.
 */
import { Alert, Button, Card } from '@fiveox/ui'
import { Download, Upload } from 'lucide-react'
import { useCallback, useRef, useState } from 'react'

import { DOCUMENT, useEvaluation } from '../lib/access'
import { ApiError, review } from '../lib/api'

interface Applied {
  products: number
  rows: number
  documents: number
  skipped_unchanged: number
  demoted_periods: string[]
}

export function HsnQueueCard({
  clientId,
  gstin,
  onApplied,
}: {
  clientId: string
  gstin: string
  onApplied?: () => void
}) {
  const { can } = useEvaluation()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [applied, setApplied] = useState<Applied | null>(null)
  const picker = useRef<HTMLInputElement>(null)

  const save = useCallback(async () => {
    setBusy('download')
    setError(null)
    try {
      const blob = await review.hsnQueue(clientId)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `hsn-codes-${gstin}.xlsx`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The queue could not be built.')
    } finally {
      setBusy(null)
    }
  }, [clientId, gstin])

  const send = useCallback(
    async (file: File) => {
      setBusy('upload')
      setError(null)
      setApplied(null)
      try {
        setApplied((await review.applyHsnQueue(clientId, file)).data)
        onApplied?.()
      } catch (err) {
        // A failure here means NOTHING was written — the service validates
        // every code before it applies any of them. Say so, because the
        // natural fear on a bulk write is that half of it landed.
        setError(
          err instanceof ApiError
            ? `${err.message} Nothing was applied.`
            : 'That file could not be applied. Nothing was changed.',
        )
      } finally {
        setBusy(null)
        if (picker.current) picker.current.value = ''
      }
    },
    [clientId, onApplied],
  )

  return (
    <Card className="mb-4 p-4">
      <p className="gst-panel-h mb-2">HSN codes to confirm</p>
      <p className="gst-meta mb-3">
        Download the products still needing a code, biggest turnover first, with
        the directory&rsquo;s own descriptions beside them. Fill the{' '}
        <strong>HSN to use</strong> column and send it back — a blank row is
        left for later, and one code the directory does not publish refuses the
        whole file rather than applying half of it.
      </p>

      {error && (
        <Alert tone="error" className="mb-3">
          {error}
        </Alert>
      )}

      {applied && (
        <Alert tone="info" className="mb-3">
          Applied <strong>{applied.products.toLocaleString()}</strong> product
          {applied.products === 1 ? '' : 's'} across{' '}
          <strong>{applied.rows.toLocaleString()}</strong> row
          {applied.rows === 1 ? '' : 's'} in {applied.documents} document
          {applied.documents === 1 ? '' : 's'}.
          {applied.skipped_unchanged > 0 && (
            <>
              {' '}
              {applied.skipped_unchanged.toLocaleString()} row
              {applied.skipped_unchanged === 1 ? '' : 's'} already carried the
              answer and {applied.skipped_unchanged === 1 ? 'was' : 'were'} left
              alone.
            </>
          )}
          {applied.demoted_periods.length > 0 && (
            <>
              {' '}
              <strong>
                {applied.demoted_periods.length} period
                {applied.demoted_periods.length === 1 ? '' : 's'} went back to
                review
              </strong>{' '}
              because this changed data they had already sent.
            </>
          )}
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" disabled={busy !== null} onClick={() => void save()}>
          <Download size={12} /> {busy === 'download' ? 'Building…' : 'Download the queue'}
        </Button>

        {can(DOCUMENT, 'can_update') && (
          <>
            <input
              ref={picker}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) void send(file)
              }}
            />
            <Button
              type="button"
              size="sm"
              disabled={busy !== null}
              onClick={() => picker.current?.click()}
            >
              <Upload size={12} /> {busy === 'upload' ? 'Applying…' : 'Send the filled sheet back'}
            </Button>
          </>
        )}
      </div>
    </Card>
  )
}
